import React, { useEffect, useState } from 'react';
import {
  Contrast,
  Eye,
  Link,
  Minus,
  Moon,
  Palette,
  PersonStanding,
  Plus,
  RotateCcw,
  Sun,
  Type,
  Underline,
  X
} from 'lucide-react';

type ContrastMode = 'default' | 'high' | 'negative' | 'light';

interface AccessibilityPreferences {
  textScale: number;
  contrastMode: ContrastMode;
  grayscale: boolean;
  underlineLinks: boolean;
  readableFont: boolean;
  reduceMotion: boolean;
}

const STORAGE_KEY = 'accessibility-preferences';
const MIN_TEXT_SCALE = -1;
const MAX_TEXT_SCALE = 3;

const defaultPreferences: AccessibilityPreferences = {
  textScale: 0,
  contrastMode: 'default',
  grayscale: false,
  underlineLinks: false,
  readableFont: false,
  reduceMotion: false
};

const readStoredPreferences = (): AccessibilityPreferences => {
  if (typeof window === 'undefined') {
    return defaultPreferences;
  }

  try {
    const storedPreferences = window.localStorage.getItem(STORAGE_KEY);
    if (!storedPreferences) {
      return defaultPreferences;
    }

    return {
      ...defaultPreferences,
      ...JSON.parse(storedPreferences)
    };
  } catch {
    return defaultPreferences;
  }
};

const AccessibilityWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [preferences, setPreferences] = useState<AccessibilityPreferences>(readStoredPreferences);

  useEffect(() => {
    const root = document.documentElement;
    const textScale = 1 + preferences.textScale * 0.1;

    root.style.setProperty('--accessibility-text-scale', textScale.toString());
    root.classList.toggle('accessibility-grayscale', preferences.grayscale);
    root.classList.toggle('accessibility-underline-links', preferences.underlineLinks);
    root.classList.toggle('accessibility-readable-font', preferences.readableFont);
    root.classList.toggle('accessibility-reduce-motion', preferences.reduceMotion);
    root.classList.toggle('accessibility-high-contrast', preferences.contrastMode === 'high');
    root.classList.toggle('accessibility-negative-contrast', preferences.contrastMode === 'negative');
    root.classList.toggle('accessibility-light-background', preferences.contrastMode === 'light');

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  }, [preferences]);

  const updatePreference = <Key extends keyof AccessibilityPreferences>(
    key: Key,
    value: AccessibilityPreferences[Key]
  ) => {
    setPreferences((currentPreferences) => ({
      ...currentPreferences,
      [key]: value
    }));
  };

  const changeTextScale = (delta: number) => {
    setPreferences((currentPreferences) => ({
      ...currentPreferences,
      textScale: Math.min(MAX_TEXT_SCALE, Math.max(MIN_TEXT_SCALE, currentPreferences.textScale + delta))
    }));
  };

  const resetPreferences = () => {
    setPreferences(defaultPreferences);
  };

  const toggleButtonClass = (active: boolean) =>
    `flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
      active
        ? 'border-primary bg-primary text-white'
        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
    }`;

  return (
    <div className="fixed bottom-4 left-4 z-[60]">
      {isOpen && (
        <div
          id="accessibility-panel"
          className="mb-3 w-[min(20rem,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-4 text-gray-900 shadow-2xl"
          role="dialog"
          aria-label="Instrumente de accesibilitate"
        >
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <PersonStanding className="h-5 w-5 text-primary" aria-hidden="true" />
              <h2 className="text-base font-bold text-primary">Accesibilitate</h2>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Închide panoul de accesibilitate"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mb-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Type className="h-4 w-4" aria-hidden="true" />
              Dimensiune text
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => changeTextScale(-1)}
                className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-2 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Micșorează textul"
              >
                <Minus className="h-4 w-4" />
              </button>
              <div className="flex items-center justify-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold">
                {Math.round((1 + preferences.textScale * 0.1) * 100)}%
              </div>
              <button
                type="button"
                onClick={() => changeTextScale(1)}
                className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-2 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Mărește textul"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid gap-2">
            <button
              type="button"
              onClick={() => updatePreference('contrastMode', preferences.contrastMode === 'high' ? 'default' : 'high')}
              className={toggleButtonClass(preferences.contrastMode === 'high')}
              aria-pressed={preferences.contrastMode === 'high'}
            >
              <span className="inline-flex items-center gap-2">
                <Contrast className="h-4 w-4" aria-hidden="true" />
                Contrast ridicat
              </span>
            </button>
            <button
              type="button"
              onClick={() => updatePreference('contrastMode', preferences.contrastMode === 'negative' ? 'default' : 'negative')}
              className={toggleButtonClass(preferences.contrastMode === 'negative')}
              aria-pressed={preferences.contrastMode === 'negative'}
            >
              <span className="inline-flex items-center gap-2">
                <Moon className="h-4 w-4" aria-hidden="true" />
                Contrast negativ
              </span>
            </button>
            <button
              type="button"
              onClick={() => updatePreference('contrastMode', preferences.contrastMode === 'light' ? 'default' : 'light')}
              className={toggleButtonClass(preferences.contrastMode === 'light')}
              aria-pressed={preferences.contrastMode === 'light'}
            >
              <span className="inline-flex items-center gap-2">
                <Sun className="h-4 w-4" aria-hidden="true" />
                Fundal deschis
              </span>
            </button>
            <button
              type="button"
              onClick={() => updatePreference('grayscale', !preferences.grayscale)}
              className={toggleButtonClass(preferences.grayscale)}
              aria-pressed={preferences.grayscale}
            >
              <span className="inline-flex items-center gap-2">
                <Palette className="h-4 w-4" aria-hidden="true" />
                Tonuri de gri
              </span>
            </button>
            <button
              type="button"
              onClick={() => updatePreference('underlineLinks', !preferences.underlineLinks)}
              className={toggleButtonClass(preferences.underlineLinks)}
              aria-pressed={preferences.underlineLinks}
            >
              <span className="inline-flex items-center gap-2">
                <Underline className="h-4 w-4" aria-hidden="true" />
                Subliniază linkurile
              </span>
            </button>
            <button
              type="button"
              onClick={() => updatePreference('readableFont', !preferences.readableFont)}
              className={toggleButtonClass(preferences.readableFont)}
              aria-pressed={preferences.readableFont}
            >
              <span className="inline-flex items-center gap-2">
                <Link className="h-4 w-4" aria-hidden="true" />
                Font lizibil
              </span>
            </button>
            <button
              type="button"
              onClick={() => updatePreference('reduceMotion', !preferences.reduceMotion)}
              className={toggleButtonClass(preferences.reduceMotion)}
              aria-pressed={preferences.reduceMotion}
            >
              <span className="inline-flex items-center gap-2">
                <Eye className="h-4 w-4" aria-hidden="true" />
                Redu animațiile
              </span>
            </button>
          </div>

          <button
            type="button"
            onClick={resetPreferences}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            Resetează
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((currentValue) => !currentValue)}
        className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white shadow-lg transition-colors hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary"
        aria-label="Deschide instrumentele de accesibilitate"
        aria-expanded={isOpen}
        aria-controls="accessibility-panel"
      >
        <PersonStanding className="h-6 w-6" />
      </button>
    </div>
  );
};

export default AccessibilityWidget;
